import {
  Dispatch,
  FormEvent,
  SetStateAction,
  useCallback,
  useRef,
  useState,
} from "react";

import { useTranslation } from "react-i18next";
import { IMaskInput } from "react-imask";
import { useNavigate } from "react-router-dom";

import Button from "@/components/Button";
import useLocale from "@/hooks/useLocale";
import { useStore } from "@/stores/index";
import { submitActivation } from "@/utils/activation";
import { isValidEmail } from "@/utils/form";
import { postalCodeMask } from "@/utils/masks";

import styles from "./styles.module.css";

type FormViewProps = {
  activationFormValues: {
    first_name: string;
    last_name: string;
    address: string;
    email: string;
    postal_code: string;
    province: string;
    marketing_opt_in: boolean;
  };
  setActivationFormValues: Dispatch<
    SetStateAction<{
      first_name: string;
      last_name: string;
      address: string;
      email: string;
      postal_code: string;
      province: string;
      marketing_opt_in: boolean;
    }>
  >;
  setRegistered: (registered: boolean) => void;
};

const FormView = ({
  activationFormValues,
  setActivationFormValues,
  setRegistered,
}: FormViewProps) => {
  const { t, i18n } = useTranslation();
  const store = useStore();
  const locale = useLocale();
  const navigate = useNavigate();

  const [formError, setFormError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [emailError, setEmailError] = useState("");

  const emailInputRef = useRef<HTMLInputElement>(null);

  const onFormSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (loading) return;

    if (!isValidEmail(activationFormValues.email)) {
      setEmailError(t("signup.invalidEmail"));

      emailInputRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      emailInputRef.current?.focus();

      return;
    }

    setEmailError("");
    setLoading(true);

    store.setUserFirstName(activationFormValues.first_name);
    store.setUserLastName(activationFormValues.last_name);
    store.setUserMarketingOptIn(activationFormValues.marketing_opt_in);

    try {
      await submitActivation({
        instance_key: store.activation_key,
        email: activationFormValues.email,
        first_name: activationFormValues.first_name,
        last_name: activationFormValues.last_name,
        postal_code: activationFormValues.postal_code,
        marketing_opt_in: store.userMarketingOptIn || false,
        locale: i18n.language as "en" | "fr",
      });
    } catch (err) {
      console.error("Form submission error:", err);
      setFormError(true);
      setLoading(false);
      return;
    }

    store.setUserEmail(activationFormValues.email);
    store.setActivationKey(null);
    setRegistered(true);
    setLoading(false);

    navigate(`/${locale}/`);
  };

  const onAccept = useCallback(
    (value: string) => {
      setActivationFormValues((prev) => ({
        ...prev,
        postal_code: value.toUpperCase(),
      }));
    },
    [setActivationFormValues],
  );

  return (
    <div className="px-[15px] pb-[60px]">
      <form className="mt-[30px]" onSubmit={onFormSubmit}>
        <h2 className="text-center text-xl uppercase font-black mb-[30px]">
          {t("signup.title")}
          <span className="inline-block text-[16px] font-light translate-y-[-3px]">
            *
          </span>
        </h2>

        <div className="flex flex-col gap-2">
          <input
            value={activationFormValues.first_name}
            onChange={(e) =>
              setActivationFormValues((prev) => ({
                ...prev,
                first_name: e.target.value,
              }))
            }
            name="first_name"
            autoComplete="given-name"
            type="text"
            className="placeholder:text-gray placeholder:text-center placeholder:text-[12px] placeholder:color-[#BDBDBD] placeholder:font-normal text-blue rounded-[5px] w-full font-semibold py-2 px-1 border-[1px] border-blue font-roboto"
            placeholder={`${t("signup.first_name")}*`}
            required
          />
          <input
            value={activationFormValues.last_name}
            onChange={(e) =>
              setActivationFormValues((prev) => ({
                ...prev,
                last_name: e.target.value,
              }))
            }
            name="last_name"
            autoComplete="family-name"
            type="text"
            className="mt-2 placeholder:text-gray placeholder:text-center placeholder:text-[12px] placeholder:color-[#BDBDBD] placeholder:font-normal text-blue rounded-[5px] w-full font-semibold py-2 px-2 border-[1px] border-blue font-roboto"
            placeholder={`${t("signup.last_name")}*`}
            required
          />
          <input
            ref={emailInputRef}
            value={activationFormValues.email}
            onChange={(e) => {
              setActivationFormValues((prev) => ({
                ...prev,
                email: e.target.value.toLowerCase().trim(),
              }));

              if (emailError) {
                setEmailError("");
              }
            }}
            onBlur={() => {
              if (
                activationFormValues.email &&
                !isValidEmail(activationFormValues.email)
              ) {
                setEmailError(t("signup.invalidEmail"));
              }
            }}
            type="email"
            name="email"
            autoComplete="email"
            className={`mt-2 placeholder:text-gray placeholder:text-center placeholder:text-[12px] placeholder:color-[#BDBDBD] placeholder:font-normal text-blue rounded-[5px] w-full font-semibold py-2 px-2 border-[1px] ${
              emailError ? "border-red" : "border-blue"
            } font-roboto`}
            placeholder={`${t("signup.email")}*`}
            required
          />
          {emailError && <p className="text-red text-xs mt-1">{emailError}</p>}

          <IMaskInput
            value={activationFormValues.postal_code}
            onAccept={onAccept}
            mask={postalCodeMask}
            name="postal_code"
            autoCapitalize="characters"
            required
            type="text"
            placeholder={`${t("signup.postal_code")}*`}
            className="mt-2 placeholder:text-gray placeholder:text-center placeholder:text-[12px] placeholder:color-[#BDBDBD] placeholder:font-normal text-blue rounded-[5px] w-full font-semibold py-2 px-2 border-[1px] border-blue font-roboto"
          />
        </div>

        <label className="checkbox flex mt-7 text-light-gray text-xs">
          <input
            checked={activationFormValues.marketing_opt_in}
            onChange={(e) =>
              setActivationFormValues((prev) => ({
                ...prev,
                marketing_opt_in: e.target.checked,
              }))
            }
            type="checkbox"
            name="marketing_opt_in"
          />
          <div className="flex flex-col gap-2 text-[10px] color-light-gray">
            <span dangerouslySetInnerHTML={{ __html: t("signup.optIn") }} />
          </div>
        </label>

        {formError && <p className="mt-2">{t("error")}</p>}

        <div className="text-center mt-6">
          <Button
            className="font-semibold"
            disabled={!store.activation_key}
            type="submit"
            loading={loading}
          >
            {t("signup.next")}
          </Button>
        </div>

        <p
          className={`font-roboto text-[10px] text-[#666] mt-[30px] mx-1 text-center no-hyphen-break break-keep ${styles.mobileNote}`}
          dangerouslySetInnerHTML={{ __html: t("intro.note") }}
        />
      </form>
    </div>
  );
};

export default FormView;
